import * as React from "react";

import { Button, Grid, Table, InputOnChangeData, Input } from "semantic-ui-react";

import { debounce } from "lodash";

import { debounceDelay } from "@teamprevent/common/filtering/constants";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { AppState, RoomListModel } from "../model";
import { roomApi } from "../roomApi";

import { Link, Query, replace } from "redux-little-router";

export const All: React.FC = () => {
    const path = useSelector((state: AppState) => state.router.pathname);
    const initialName = useSelector((state: AppState) => state.router?.query?.name ?? "");
    const initialPostcode = useSelector((state: AppState) => state.router?.query?.postcode ?? "");

    const [rooms, setRooms] = React.useState<RoomListModel[]>([]);
    const [name, setName] = React.useState(initialName);
    const [postcode, setPostcode] = React.useState(initialPostcode);

    const dispatch = useDispatch();

    const getRoomsDebounced = React.useMemo(() => {
        return debounce(
            async (n?: string, pc?: string) => {
                const users = await roomApi.getAll({ name: n, postcode: pc });
                setRooms(users);
            }, debounceDelay);
    }, []);

    React.useEffect(() => {
        const query: Query = {};

        // Clear the query params first before setting new ones
        dispatch(replace({ pathname: "", query }, { persistQuery: false }));

        if (name) {
            query.name = name;
        }

        if (postcode) {
            query.postcode = postcode;
        }

        dispatch(replace({ pathname: "", query }, { persistQuery: true }));
    }, [dispatch, name, postcode]);

    React.useEffect(() => {
        getRoomsDebounced(name, postcode);
    }, [getRoomsDebounced, name, postcode]);

    const onNameFilterChange = React.useCallback((event: any, { value }: InputOnChangeData) => {
        setName(value);
    }, []);
    const onPostcodeFilterChange = React.useCallback((event: any, { value }: InputOnChangeData) => {
        setPostcode(value);
    }, []);

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column width={14}>
                    <h1>Rooms</h1>
                </Grid.Column>
                <Grid.Column width={2} textAlign="right">
                    <Button icon="add" content="Create" as={Link} href={`${path}/create`} />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Name</Table.HeaderCell>
                                <Table.HeaderCell>Address</Table.HeaderCell>
                                <Table.HeaderCell>Postcode</Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell>
                                    <Input
                                        value={name}
                                        onChange={onNameFilterChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell></Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={postcode}
                                        onChange={onPostcodeFilterChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {rooms && rooms
                                .map(room => (
                                    <Table.Row key={room.id}>
                                        <Table.Cell>
                                            <Link href={`${path}/${room.id}`}>
                                                {room.name}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {roomToAddressCellContent(room)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {room.postcode}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                        </Table.Body>
                    </Table>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

const roomToAddressCellContent = (room: RoomListModel): string => {
    const addressParts = [room.addressLine1];
    if (room.addressLine2 && room.addressLine2.length > 0) {
        addressParts.push(room.addressLine2);
    }
    if (room.addressLine3 && room.addressLine3.length > 0) {
        addressParts.push(room.addressLine3);
    }
    if (room.city && room.city.length > 0) {
        addressParts.push(room.city);
    }
    if (room.county && room.county.length > 0) {
        addressParts.push(room.county);
    }

    return addressParts.join(", ");
};
